import VersusPage, {
  VersusRow,
  VersusFaq,
  VersusVendor,
} from '@comp/compare/versus-template'

type VersusContent = {
  seoTitle: string
  seoDescription: string
  heroEyebrow: string
  heroTitleLines: [string, string]
  heroBody: string
  vendorA: VersusVendor
  vendorB: VersusVendor
  rows: VersusRow[]
  whenA: string[]
  whenB: string[]
  thirdOptionTitleLines: [string, string]
  thirdOptionBody: string
  thirdOptionPoints: string[]
  faqs: VersusFaq[]
}

const content: Record<'en' | 'tr', VersusContent> = {
  en: {
    seoTitle: 'CyberArk vs Delinea (2026), and the third option | Monofor',
    seoDescription:
      'An honest CyberArk vs Delinea comparison: vault depth, deployment effort, pricing models, and where a unified identity platform like Monofor fits.',
    heroEyebrow: 'PAM comparison',
    heroTitleLines: ['CyberArk vs Delinea.', 'And the question both skip.'],
    heroBody:
      'Two strong PAM vendors, two different weight classes. Here is how they actually differ, and why the more interesting question is whether your PAM should be a separate product at all.',
    vendorA: {
      name: 'CyberArk',
      summary:
        'The long-standing PAM market leader, built for large enterprises with deep privileged-security requirements, and the budgets and teams to match.',
      strengths: [
        'Deepest secrets-management and application-to-application credential portfolio',
        'Mature session isolation and threat-analytics tooling',
        'Large partner and integration ecosystem',
        'Strong analyst positioning and enterprise references',
      ],
      considerations: [
        'Cost and licensing complexity are the most-cited objections',
        'Deployments typically lean on professional services',
        'Identity and governance arrived via acquisitions: separate consoles and SKUs',
        'Operational overhead is sized for large security teams',
      ],
    },
    vendorB: {
      name: 'Delinea',
      summary:
        'A PAM specialist popular in the mid-market, centered on Secret Server’s vault with a cloud-first platform layer added around it.',
      strengths: [
        'Approachable vault with a shorter learning curve',
        'Faster initial deployment than legacy enterprise PAM',
        'Established mid-market install base',
        'On-prem Secret Server option is well proven',
      ],
      considerations: [
        'Scope is PAM-first; identity and governance need other vendors',
        'Pricing is quote-based, typically per user',
        'Advanced capabilities spread across platform tiers',
        'No enterprise SSO catalog of its own',
      ],
    },
    rows: [
      {
        dimension: 'Core vault & rotation',
        a: 'Yes, deep',
        b: 'Yes',
        monofor: 'Yes',
      },
      {
        dimension: 'Session recording',
        detail: 'Video + keystroke audit of privileged sessions',
        a: 'Yes',
        b: 'Yes',
        monofor: 'Yes: video + transcript',
      },
      {
        dimension: 'Just-in-time access',
        a: 'Yes',
        b: 'Yes',
        monofor: 'Yes',
      },
      {
        dimension: 'Secrets management depth',
        detail: 'A2A credentials, DevOps secrets',
        a: 'Market-leading',
        b: 'Moderate',
        monofor: 'Core vaulting & rotation',
      },
      {
        dimension: 'IAM in the same platform',
        detail: 'SSO, MFA, lifecycle',
        a: 'Separate product line',
        b: 'No',
        monofor: 'Native: 7,000+ app catalog',
      },
      {
        dimension: 'IGA in the same platform',
        detail: 'Access reviews, SoD',
        a: 'Via acquisition',
        b: 'No',
        monofor: 'Native',
      },
      {
        dimension: 'Self-hosted deployment',
        a: 'Yes',
        b: 'Yes (Secret Server)',
        monofor: 'Yes, same price as SaaS',
      },
      {
        dimension: 'Kubernetes & OpenShift access',
        a: 'Varies',
        b: 'Varies',
        monofor: 'Yes: native kubectl / oc, JIT tokens',
      },
      {
        dimension: 'Database sessions with dynamic masking',
        a: 'Varies',
        b: 'Varies',
        monofor: 'Yes: SQL-level policy + masking',
      },
      {
        dimension: 'Certificate lifecycle management',
        a: 'Separate platform (Venafi)',
        b: 'No',
        monofor: 'Built in',
      },
      {
        dimension: 'Typical deployment effort',
        a: 'PS engagement, months',
        b: 'Weeks',
        monofor: 'Days: agentless, browser-based',
      },
      {
        dimension: 'Pricing model',
        a: 'Quote-based, per user/module',
        b: 'Quote-based, per user',
        monofor: 'Per concurrent session; IAM from $4/user/mo',
      },
      {
        dimension: 'Public entry pricing',
        a: 'No',
        b: 'No',
        monofor: 'Yes',
      },
    ],
    whenA: [
      'You run application-to-application secrets at DevOps scale and need the deepest tooling available.',
      'You have a dedicated PAM team and the budget for a services-led enterprise deployment.',
      'Analyst-quadrant positioning matters to your procurement process.',
    ],
    whenB: [
      'You want a proven standalone vault with a shorter runway than legacy enterprise PAM.',
      'Your scope is PAM only, and identity is already settled elsewhere.',
      'Mid-market references and packaging fit your size.',
    ],
    thirdOptionTitleLines: ['Or skip the standalone', 'PAM product entirely.'],
    thirdOptionBody:
      'Monopam covers the privileged-access core this comparison is about (vault, rotation, video-recorded sessions, just-in-time approvals) inside a platform that also ships SSO for 7,000+ apps, MFA, lifecycle, and access reviews. One console, one bill, per-concurrent-session pricing.',
    thirdOptionPoints: [
      'PAM + IAM + IGA in one platform instead of two or three vendors',
      'Licensed per concurrent session, not per admin, target, or credential',
      'Self-hosted or cloud at the same price, deployed in minutes',
    ],
    faqs: [
      {
        q: 'CyberArk vs Delinea: which is better for PAM?',
        a: 'It depends on scale and depth. CyberArk leads on secrets management, session isolation depth, and enterprise ecosystem; Delinea is faster to stand up and friendlier to mid-market teams. If your requirement is disciplined human privileged access (vault, rotation, recording, JIT), both clear the bar, and so does a leaner platform like Monopam at a very different cost profile.',
      },
      {
        q: 'Where does Monofor fit into this comparison?',
        a: 'Monofor is the consolidation play: Monopam covers the PAM core both vendors compete on, while the same platform ships SSO, MFA, lifecycle, and governance that both vendors would leave to additional products. For teams whose PAM needs are human-access-centric, one platform replaces two or three line items.',
      },
      {
        q: 'When would Monofor NOT be the right choice?',
        a: 'If your primary requirement is deep application-to-application secrets management at DevOps scale (vaulting credentials inside CI/CD pipelines across thousands of services), CyberArk’s dedicated tooling is the benchmark. Evaluate that workload specifically during a trial.',
      },
      {
        q: 'Can we migrate from CyberArk or Delinea gradually?',
        a: 'Yes. Credentials import into the Monopam vault, resources are discovered from AD, Azure, and AWS, and browser-based sessions need no endpoint rollout. Teams typically migrate one privilege domain at a time and retire the incumbent at renewal.',
      },
    ],
  },
  tr: {
    seoTitle: 'CyberArk vs Delinea (2026) ve üçüncü seçenek | Monofor',
    seoDescription:
      'Dürüst bir CyberArk vs Delinea karşılaştırması: kasa derinliği, kurulum eforu, fiyatlandırma modelleri ve Monofor gibi birleşik bir kimlik platformunun konumu.',
    heroEyebrow: 'PAM karşılaştırması',
    heroTitleLines: ['CyberArk vs Delinea.', 'Ve ikisinin de atladığı soru.'],
    heroBody:
      'İki güçlü PAM tedarikçisi, iki farklı ağırlık sınıfı. İşte gerçekte nasıl ayrıştıkları ve asıl ilginç soru: PAM’iniz gerçekten ayrı bir ürün olmak zorunda mı?',
    vendorA: {
      name: 'CyberArk',
      summary:
        'Uzun süredir PAM pazarının lideri; derin ayrıcalıklı güvenlik gereksinimleri olan (ve buna uygun bütçe ve ekiplere sahip) büyük kuruluşlar için tasarlandı.',
      strengths: [
        'En derin gizli bilgi yönetimi ve uygulamadan uygulamaya kimlik bilgisi portföyü',
        'Olgun oturum izolasyonu ve tehdit analitiği araçları',
        'Geniş iş ortağı ve entegrasyon ekosistemi',
        'Güçlü analist konumlandırması ve kurumsal referanslar',
      ],
      considerations: [
        'Maliyet ve lisanslama karmaşıklığı en sık dile getirilen itirazlar',
        'Kurulumlar genellikle profesyonel hizmetlere dayanır',
        'Kimlik ve yönetişim satın almalarla eklendi: ayrı konsollar ve SKU’lar',
        'Operasyonel yük büyük güvenlik ekiplerine göre boyutlandırılmış',
      ],
    },
    vendorB: {
      name: 'Delinea',
      summary:
        'Orta ölçekli pazarda popüler bir PAM uzmanı; merkezinde Secret Server kasası, çevresinde bulut öncelikli bir platform katmanı bulunur.',
      strengths: [
        'Öğrenme eğrisi kısa, erişilebilir bir kasa',
        'Eski nesil kurumsal PAM’e göre daha hızlı ilk kurulum',
        'Yerleşik bir orta ölçekli pazar kurulum tabanı',
        'Şirket içi Secret Server seçeneği kendini kanıtlamış',
      ],
      considerations: [
        'Kapsam PAM öncelikli; kimlik ve yönetişim için başka tedarikçiler gerekir',
        'Fiyatlandırma teklife dayalı, genellikle kullanıcı başına',
        'Gelişmiş yetenekler platform katmanlarına dağılmış',
        'Kendine ait kurumsal SSO kataloğu yok',
      ],
    },
    rows: [
      {
        dimension: 'Çekirdek kasa ve rotasyon',
        a: 'Evet, derin',
        b: 'Evet',
        monofor: 'Evet',
      },
      {
        dimension: 'Oturum kaydı',
        detail: 'Ayrıcalıklı oturumların video + tuş vuruşu denetimi',
        a: 'Evet',
        b: 'Evet',
        monofor: 'Evet: video + transkript',
      },
      {
        dimension: 'Just-in-time erişim',
        a: 'Evet',
        b: 'Evet',
        monofor: 'Evet',
      },
      {
        dimension: 'Gizli bilgi yönetimi derinliği',
        detail: 'A2A kimlik bilgileri, DevOps gizli bilgileri',
        a: 'Pazar lideri',
        b: 'Orta düzey',
        monofor: 'Çekirdek kasalama ve rotasyon',
      },
      {
        dimension: 'Aynı platformda IAM',
        detail: 'SSO, MFA, yaşam döngüsü',
        a: 'Ayrı ürün hattı',
        b: 'Hayır',
        monofor: 'Yerleşik: 7.000+ uygulama kataloğu',
      },
      {
        dimension: 'Aynı platformda IGA',
        detail: 'Erişim incelemeleri, görev ayrılığı (SoD)',
        a: 'Satın alma yoluyla',
        b: 'Hayır',
        monofor: 'Yerleşik',
      },
      {
        dimension: 'Kendi altyapınızda kurulum',
        a: 'Evet',
        b: 'Evet (Secret Server)',
        monofor: 'Evet, SaaS ile aynı fiyat',
      },
      {
        dimension: 'Kubernetes ve OpenShift erişimi',
        a: 'Değişken',
        b: 'Değişken',
        monofor: 'Evet: yerel kubectl / oc, JIT token',
      },
      {
        dimension: 'Dinamik maskelemeli veritabanı oturumları',
        a: 'Değişken',
        b: 'Değişken',
        monofor: 'Evet: SQL seviyesinde politika + maskeleme',
      },
      {
        dimension: 'Sertifika yaşam döngüsü yönetimi',
        a: 'Ayrı platform (Venafi)',
        b: 'Hayır',
        monofor: 'Yerleşik',
      },
      {
        dimension: 'Tipik kurulum eforu',
        a: 'Profesyonel hizmet projesi, aylar',
        b: 'Haftalar',
        monofor: 'Günler: ajansız, tarayıcı tabanlı',
      },
      {
        dimension: 'Fiyatlandırma modeli',
        a: 'Teklife dayalı, kullanıcı/modül başına',
        b: 'Teklife dayalı, kullanıcı başına',
        monofor: 'Eşzamanlı oturum başına; IAM 4 $/kullanıcı/ay’dan başlar',
      },
      {
        dimension: 'Açık giriş fiyatı',
        a: 'Hayır',
        b: 'Hayır',
        monofor: 'Evet',
      },
    ],
    whenA: [
      'Uygulamadan uygulamaya gizli bilgileri DevOps ölçeğinde yönetiyorsunuz ve mevcut en derin araçlara ihtiyacınız var.',
      'Özel bir PAM ekibiniz ve hizmet ağırlıklı kurumsal bir kurulum için bütçeniz var.',
      'Analist çeyreği konumlandırması satın alma süreciniz için önemli.',
    ],
    whenB: [
      'Eski nesil kurumsal PAM’den daha kısa sürede devreye alınan, kendini kanıtlamış bağımsız bir kasa istiyorsunuz.',
      'Kapsamınız yalnızca PAM ve kimlik tarafı zaten başka bir yerde çözülmüş.',
      'Orta ölçekli pazar referansları ve paketleme, kurum büyüklüğünüze uygun.',
    ],
    thirdOptionTitleLines: [
      'Ya da bağımsız PAM ürününü',
      'tamamen atlayın.',
    ],
    thirdOptionBody:
      'Monopam, bu karşılaştırmanın konusu olan ayrıcalıklı erişim çekirdeğini (kasa, rotasyon, video kayıtlı oturumlar, just-in-time onaylar) kapsar; aynı platform 7.000+ uygulama için SSO, MFA, yaşam döngüsü ve erişim incelemelerini de birlikte sunar. Tek konsol, tek fatura, eşzamanlı oturum başına fiyatlandırma.',
    thirdOptionPoints: [
      'İki veya üç tedarikçi yerine tek platformda PAM + IAM + IGA',
      'Eşzamanlı oturum başına lisanslama; yönetici, hedef veya kimlik bilgisi başına değil',
      'Kendi altyapınızda veya bulutta aynı fiyat, dakikalar içinde kurulum',
    ],
    faqs: [
      {
        q: 'CyberArk vs Delinea: PAM için hangisi daha iyi?',
        a: 'Ölçeğe ve derinliğe bağlı. CyberArk gizli bilgi yönetimi, oturum izolasyonu derinliği ve kurumsal ekosistemde önde; Delinea daha hızlı devreye alınır ve orta ölçekli ekipler için daha erişilebilirdir. Gereksiniminiz disiplinli insan kaynaklı ayrıcalıklı erişimse (kasa, rotasyon, kayıt, JIT) ikisi de çıtayı geçer; Monopam gibi daha yalın bir platform da bunu çok farklı bir maliyet profiliyle yapar.',
      },
      {
        q: 'Monofor bu karşılaştırmanın neresinde?',
        a: 'Monofor konsolidasyon seçeneğidir: Monopam, iki tedarikçinin rekabet ettiği PAM çekirdeğini kapsar; aynı platform, her iki tedarikçinin de ek ürünlere bırakacağı SSO, MFA, yaşam döngüsü ve yönetişimi birlikte sunar. PAM ihtiyacı insan erişimi odaklı ekipler için tek platform, iki veya üç kalemin yerini alır.',
      },
      {
        q: 'Monofor ne zaman doğru seçim OLMAZ?',
        a: 'Birincil gereksiniminiz DevOps ölçeğinde derin uygulamadan uygulamaya gizli bilgi yönetimiyse (binlerce servise yayılan CI/CD hatlarındaki kimlik bilgilerini kasalamak) CyberArk’ın özel araçları bu alanda ölçüttür. Deneme sürecinde bu iş yükünü özellikle değerlendirin.',
      },
      {
        q: 'CyberArk veya Delinea’dan kademeli olarak geçiş yapabilir miyiz?',
        a: 'Evet. Kimlik bilgileri Monopam kasasına içe aktarılır, kaynaklar AD, Azure ve AWS’den keşfedilir ve tarayıcı tabanlı oturumlar uç nokta dağıtımı gerektirmez. Ekipler genellikle ayrıcalık alanlarını tek tek taşır ve yenileme döneminde mevcut ürünü devreden çıkarır.',
      },
    ],
  },
}

type Props = {
  locale: string
}

export default function CyberArkVsDelineaPage({ locale }: Props) {
  const c = content[locale === 'tr' ? 'tr' : 'en']
  return (
    <VersusPage
      locale={locale}
      bilingual
      path="/compare/cyberark-vs-delinea"
      seoTitle={c.seoTitle}
      seoDescription={c.seoDescription}
      heroEyebrow={c.heroEyebrow}
      heroTitle={
        <>
          {c.heroTitleLines[0]}
          <br />
          {c.heroTitleLines[1]}
        </>
      }
      heroBody={c.heroBody}
      vendorA={c.vendorA}
      vendorB={c.vendorB}
      rows={c.rows}
      whenA={c.whenA}
      whenB={c.whenB}
      thirdOptionTitle={
        <>
          {c.thirdOptionTitleLines[0]}
          <br />
          {c.thirdOptionTitleLines[1]}
        </>
      }
      thirdOptionBody={c.thirdOptionBody}
      thirdOptionPoints={c.thirdOptionPoints}
      faqs={c.faqs}
    />
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
